@if (failed) {
  <p class="page muted">{{ 'invoice.not_found' | translate }}</p>
}
@if (invoice; as inv) {
  <div class="pinned">
    <div class="title">
      <h1>{{ (inv.credits ? 'invoice.credit_note' : 'invoice.title') | translate }} {{ inv.number ?? ('invoice.draft_label' | translate:{id: inv.id}) }}</h1>
      <bst-invoice-status [invoice]="inv"></bst-invoice-status>
    </div>
    <div class="buttons">
      @if (draft) {
        <button id="invoice-finalise-btn" nbButton size="small" shape="semi-round" status="primary" [disabled]="busy || !!blocking.length || !!inv.missing?.length || unpriced"
                [title]="(blocking.length ? 'invoice.issue.finalise_blocked' : inv.missing?.length || unpriced ? 'invoice.issue.finalise_incomplete' : '') | translate"
                [attr.aria-describedby]="blocking.length ? 'invoice-must-fix-heading' : null" (click)="finalise()">{{ 'invoice.finalise' | translate }}</button>
      } @else if (inv.pdf_stored) {
        @if (creditable) {
          <button nbButton size="small" shape="semi-round" status="basic" ghost [disabled]="busy" (click)="credit()">{{ 'invoice.credit' | translate }}</button>
        }
        <button nbButton size="small" shape="semi-round" status="basic" (click)="downloadUbl()">{{ 'invoice.download_ubl' | translate }}</button>
        <button nbButton size="small" shape="semi-round" status="primary" (click)="download()">{{ 'invoice.download' | translate }}</button>
      } @else {
        <button nbButton size="small" shape="semi-round" status="primary" [disabled]="busy" (click)="finalise()">{{ 'invoice.retry_pdf' | translate }}</button>
      }
    </div>
  </div>

  <article class="page">
    <dl class="meta">
      <div><dt>{{ 'invoice.client' | translate }}</dt><dd>
        @if (canLinkClient) { <a [routerLink]="['/clients', inv.client_id]">{{ inv.client_name }}</a> } @else { {{ inv.client_name }} }
      </dd></div>
      <div><dt>{{ 'invoice.project' | translate }}</dt><dd><a [routerLink]="['/projects', inv.project.id]">{{ inv.project.name }}</a></dd></div>
      <div><dt>{{ 'invoice.period' | translate }}</dt><dd>{{ period }}</dd></div>
      @if (inv.reference) { <div><dt>{{ 'invoicing.project.client_reference' | translate }}</dt><dd>{{ inv.reference }}</dd></div> }
      @if (!draft) {
        <div><dt>{{ (inv.credits ? 'invoice.date' : 'invoice.invoice_date') | translate }}</dt><dd>{{ inv.invoice_date | localedate:'D MMM YYYY' }}</dd></div>
        @if (inv.due_date) { <div><dt>{{ 'invoice.due_date' | translate }}</dt><dd>{{ inv.due_date | localedate:'D MMM YYYY' }}</dd></div> }
      }
      @if (inv.credits) { <div><dt>{{ 'invoice.credits' | translate }}</dt><dd><a [routerLink]="['/invoices', inv.credits.id]">{{ inv.credits.number }}</a></dd></div> }
      @if (inv.credited_by) { <div><dt>{{ 'invoice.credited_by' | translate }}</dt><dd><a [routerLink]="['/invoices', inv.credited_by.id]">{{ inv.credited_by.number }}</a></dd></div> }
    </dl>

    @if (!draft && !inv.pdf_stored) {
      <p class="notice warning"><nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>{{ 'invoice.pdf_pending' | translate:{number: inv.number} }}</p>
    }
    @if (!inv.issues) {
      <!-- Fallback for a backend that has not started emitting `issues` yet (#1518 PR8b): the
           sections below replace these notices once it does, so both must never render together. -->
      @if (inv.missing?.length) {
        <div class="notice warning">
          <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
          <div>
            <span>{{ 'invoicing.project.missing' | translate }}</span>
            <ul>@for (field of inv.missing; track field) { <li>{{ 'invoicing.missing.' + field | translate }}</li> }</ul>
          </div>
        </div>
      }
      @if (defaultRate; as d) {
        <div class="notice warning">
          <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
          <div>
            <span>{{ 'invoice.warning.default_rate' | translate:{rate: d.rate} }}</span>
            <ul>@for (l of inv.warnings.default_rate; track $index) { <li>{{ l.description }} ({{ l.hundredths / 100 | number:'1.2-2':lang }} {{ 'invoice.unit_hour' | translate }})</li> }</ul>
          </div>
        </div>
      }
      @if (beforeStart.length) {
        <div class="notice warning">
          <nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>
          <div>
            <span>{{ 'invoice.warning.before_start' | translate:{date: inv.period_from | localedate:'D MMM YYYY'} }}</span>
            <ul>@for (b of beforeStart; track $index) { <li>{{ b.person }}, {{ b.period }}: {{ b.work }}</li> }</ul>
          </div>
        </div>
      }
      @if (inv.warnings?.no_einvoice) {
        <p class="notice warning"><nb-icon icon="alert-triangle-outline" status="warning"></nb-icon>{{ 'invoice.warning.no_einvoice' | translate:{client: inv.client_name} }}</p>
      }
      @for (w of warnings; track w.key) {
        <p class="notice"><nb-icon icon="info-outline" status="info"></nb-icon>{{ 'invoice.warning.' + w.key | translate:{value: w.value} }}</p>
      }
      @if (inv.warnings?.orphan?.length) {
        <div class="notice">
          <nb-icon icon="info-outline" status="info"></nb-icon>
          <div>
            <span>{{ 'invoice.warning.orphan' | translate }}</span>
            <ul>@for (o of inv.warnings.orphan; track $index) { <li>{{ o.person }}, {{ 'invoice.warning.orphan_week' | translate:{date: o.week_start | localedate:'D MMM YYYY'} }}: {{ o.hundredths / 100 | number:'1.2-2':lang }} {{ 'invoice.unit_hour' | translate }}</li> }</ul>
          </div>
        </div>
      }
    }

    <ng-template #actionLink let-a="a">
      @if (a.local) {
        <button type="button" class="link-like" (click)="focusLocal(a)">{{ 'invoice.issue.' + a.label | translate:a.params }}</button>
      } @else {
        <a [routerLink]="a.link" [queryParams]="a.queryParams">{{ 'invoice.issue.' + a.label | translate:a.params }}</a>
      }
    </ng-template>
    <ng-template #issueLine let-issue="issue">
      @if (action(issue); as a) {
        @if (a === 'admin') {
          <span>{{ isReadiness(issue.code) ? (('invoice.issue.fix.' + issue.code) | translate) : issueText(issue) }}</span>
          <span class="admin">{{ 'invoice.issue.admin_required' | translate }}</span>
        } @else if (isReadiness(issue.code)) {
          @if (!duplicatesFix(issue, a)) {
            <span>{{ 'invoice.issue.fix.' + issue.code | translate }}</span>
          }
          @if (isArray(a)) {
            @for (one of a; track one.label) { <ng-container *ngTemplateOutlet="actionLink; context: {a: one}"></ng-container> }
          } @else {
            <ng-container *ngTemplateOutlet="actionLink; context: {a: a}"></ng-container>
          }
        } @else {
          <span>{{ issueText(issue) }}</span>
          @if (isArray(a)) {
            @for (one of a; track one.label) { <ng-container *ngTemplateOutlet="actionLink; context: {a: one}"></ng-container> }
          } @else {
            <ng-container *ngTemplateOutlet="actionLink; context: {a: a}"></ng-container>
          }
        }
      } @else {
        <span>{{ isReadiness(issue.code) ? (('invoice.issue.fix.' + issue.code) | translate) : issueText(issue) }}</span>
      }
    </ng-template>
    @if (draft && (issues.blocking.length || issues.review.length || issues.info.length)) {
      <section class="card checks">
        <h2><nb-icon icon="checkmark-square-2-outline"></nb-icon>{{ 'invoice.issue.checks' | translate }}</h2>
        @if (issues.blocking.length) {
          <section class="issues blocking">
            <h3 id="invoice-must-fix-heading">{{ 'invoice.issue.must_fix' | translate }} <span class="count">{{ issues.blocking.length }}</span></h3>
            <ul>
              @for (issue of issues.blocking; track issue.code + $index) {
                <li><ng-container *ngTemplateOutlet="issueLine; context: {issue: issue}"></ng-container></li>
              }
            </ul>
          </section>
        }
        @if (issues.review.length) {
          <section class="issues review">
            <h3>{{ 'invoice.issue.review' | translate }} <span class="count">{{ issues.review.length }}</span></h3>
            <ul>
              @for (issue of issues.review; track issue.code + $index) {
                <li><ng-container *ngTemplateOutlet="issueLine; context: {issue: issue}"></ng-container></li>
              }
            </ul>
          </section>
        }
        @if (issues.info.length) {
          <details class="issues info">
            <summary><h3>{{ 'invoice.issue.not_included' | translate }} <span class="count">{{ issues.info.length }}</span><nb-icon class="chevron" icon="chevron-right-outline"></nb-icon></h3></summary>
            <ul>
              @for (issue of issues.info; track issue.code + $index) {
                <li><ng-container *ngTemplateOutlet="issueLine; context: {issue: issue}"></ng-container></li>
              }
            </ul>
          </details>
        }
      </section>
    }

    <section class="card lines">
      <h2><nb-icon icon="list-outline"></nb-icon>{{ 'invoice.lines' | translate }}</h2>
      @if (draft) {
        @if (introOpen || inv.intro) {
          <div class="intro">
            <label for="invoice-intro"><span class="hint-tip" tabindex="0" [nbTooltip]="'invoice.intro_hint' | translate">{{ 'invoice.intro' | translate }}</span></label>
            <textarea id="invoice-intro" nbInput fullWidth [rows]="introRows" maxlength="500" [attr.aria-description]="'invoice.intro_hint' | translate" [status]="pending.has('intro') ? 'danger' : 'basic'" [(ngModel)]="intro" (input)="grow($any($event.target))" (blur)="saveIntro()"></textarea>
          </div>
        } @else {
          <button type="button" class="link-like add-intro" (click)="openIntro()">{{ 'invoice.intro_add' | translate }}</button>
        }
      } @else if (inv.intro) {
        <p class="intro-text">{{ inv.intro }}</p>
      }
      <div class="scroll">
        <table class="stack-narrow">
          <thead><tr>
            <th>{{ 'invoice.description' | translate }}</th><th class="num">{{ 'invoice.quantity' | translate }}</th>
            <th>{{ 'invoice.unit' | translate }}</th><th class="num">{{ 'invoice.unit_price' | translate }}</th><th class="num">{{ 'invoice.amount' | translate }}</th>
          </tr></thead>
          <ng-template #lineRow let-line="line">
            <!-- A credit note's lines hold no entries: they stay with the invoice it credits. -->
            <tr [class.off]="!line.entries && !inv.credits">
              <td>
                @if (draft) {
                  <input nbInput fieldSize="small" fullWidth class="description" maxlength="255" [attr.aria-label]="'invoice.description' | translate" [status]="pending.has('description' + line.id) ? 'danger' : 'basic'" [value]="line.description" (input)="described.add(line.id)" (blur)="saveDescription(line, $any($event.target))" (keyup.enter)="$any($event.target).blur()">
                } @else { {{ line.description }} }
                @if (!line.entries && !inv.credits) { <span class="hint">{{ 'invoice.line_off' | translate }}</span> }
              </td>
              <td class="num" [attr.data-label]="'invoice.quantity' | translate">{{ line.quantity / 100 | number:'1.2-2':lang }}</td>
              <td class="muted" [attr.data-label]="'invoice.unit' | translate">{{ unit(line) | translate }}</td>
              <td class="num" [attr.data-label]="'invoice.unit_price' | translate">
                @if (draft) {
                  <input [id]="'line-' + line.id + '-price'" nbInput fieldSize="small" class="price" inputmode="decimal" [status]="pending.has('price' + line.id) || line.entries && !line.has_price ? 'danger' : 'basic'" [placeholder]="'invoice.no_price' | translate" [attr.aria-label]="'invoice.unit_price' | translate" [value]="priceInput(line.unit_price)" (input)="typed.add(line.id)" (blur)="savePrice(line, $any($event.target))" (keyup.enter)="$any($event.target).blur()">
                } @else { {{ line.unit_price | currency:'EUR':'symbol':'1.2-4':lang }} }
              </td>
              <td class="num" [attr.data-label]="'invoice.amount' | translate">@if (line.has_price) { {{ line.amount_cents / 100 | currency:'EUR':'symbol':'1.2-2':lang }} } @else { <span class="danger">{{ 'invoice.no_price' | translate }}</span> }</td>
            </tr>
          </ng-template>
          <!-- A group heading only tells two kinds apart; a draft of hours alone needs none. -->
          @if (hourLines.length) {
            <tbody>
              @if (kmLines.length) { <tr class="grp"><th colspan="5" scope="rowgroup">{{ 'registration.hours' | translate }}</th></tr> }
              @for (line of hourLines; track line.id) { <ng-container *ngTemplateOutlet="lineRow; context: {line: line}"></ng-container> }
            </tbody>
          }
          @if (kmLines.length) {
            <tbody>
              @if (hourLines.length) { <tr class="grp"><th colspan="5" scope="rowgroup">{{ 'invoicing.km.title' | translate }}</th></tr> }
              @for (line of kmLines; track line.id) { <ng-container *ngTemplateOutlet="lineRow; context: {line: line}"></ng-container> }
            </tbody>
          }
          <!-- The totals close the Amount column; whitespace between label and amount keeps them two words in text. -->
          <tfoot>
            @if (draft) {
              <tr><th colspan="4" scope="row">{{ 'invoice.subtotal' | translate }} </th>
                <td class="num">{{ net / 100 | currency:'EUR':'symbol':'1.2-2':lang }}</td></tr>
              @if (inv.vat_preview_cents != null) {
                <tr><th colspan="4" scope="row">{{ 'invoice.vat' | translate }} @if (!reverseCharge) { {{ inv.vat_preview_rate }}% }</th>
                  <td class="num">@if (reverseCharge) { {{ 'invoice.vat_reverse_charge' | translate }} } @else { {{ inv.vat_preview_cents / 100 | currency:'EUR':'symbol':'1.2-2':lang }} }</td></tr>
                <tr class="grand"><th colspan="4" scope="row">{{ 'invoice.total_incl' | translate }} </th>
                  <td class="num">{{ gross / 100 | currency:'EUR':'symbol':'1.2-2':lang }}</td></tr>
              }
              <tr class="note"><td colspan="5">{{ (reverseCharge ? 'invoice.reverse_charge_on_finalise' : inv.vat_preview_cents != null ? 'invoice.vat_preview' : 'invoice.vat_on_finalise') | translate }}</td></tr>
            } @else {
              <tr><th colspan="4" scope="row">{{ 'invoice.subtotal' | translate }} </th>
                <td class="num">{{ inv.net_cents / 100 | currency:'EUR':'symbol':'1.2-2':lang }}</td></tr>
              <tr><th colspan="4" scope="row">{{ 'invoice.vat' | translate }} @if (!reverseCharge && finalRate != null) { {{ finalRate | number:'1.0-2':lang }}% }</th>
                <td class="num">@if (reverseCharge) { {{ 'invoice.vat_reverse_charge' | translate }} } @else { {{ inv.vat_cents / 100 | currency:'EUR':'symbol':'1.2-2':lang }} }</td></tr>
              <tr class="grand"><th colspan="4" scope="row">{{ 'invoice.total_incl' | translate }} </th>
                <td class="num">{{ inv.gross_cents / 100 | currency:'EUR':'symbol':'1.2-2':lang }}</td></tr>
            }
          </tfoot>
        </table>
      </div>
    </section>

    @if (inv.trips.length || inv.not_chargeable?.length) {
      <details class="card trips">
        <summary><h2><nb-icon icon="car-outline"></nb-icon>{{ 'invoice.trips' | translate }} <span class="count">{{ inv.trips.length + (inv.not_chargeable?.length ?? 0) }}</span>
          @if (tripCounts.off) {<span class="summary-note">{{ 'invoice.trips_summary' | translate:tripCounts }}</span>}<nb-icon class="chevron" icon="chevron-right-outline"></nb-icon></h2></summary>
        <div class="scroll">
          <table class="stack-narrow">
            <thead><tr>
              <th>{{ 'invoice.date' | translate }}</th><th>{{ 'invoice.person' | translate }}</th><th>{{ 'invoice.project' | translate }}</th>
              <th>{{ 'invoice.from' | translate }}</th><th>{{ 'invoice.to' | translate }}</th><th class="num">{{ 'invoice.km' | translate }}</th><th>{{ 'invoice.charged' | translate }}</th>
            </tr></thead>
            <tbody>
              @for (trip of inv.trips; track trip.id) {
                <tr [class.off]="!trip.included">
                  <td class="nowrap">{{ trip.date | localedate:'ddd D MMM' }}</td><td [attr.data-label]="'invoice.person' | translate">{{ trip.person }}</td><td [attr.data-label]="'invoice.project' | translate">{{ trip.project }}</td>
                  <td [attr.data-label]="'invoice.from' | translate">{{ trip.from_address }}</td><td [attr.data-label]="'invoice.to' | translate">{{ trip.to_address }}</td><td class="num" [attr.data-label]="'invoice.km' | translate">{{ trip.km_tenths / 10 | number:'1.1-1':lang }}</td>
                  <td [attr.data-label]="'invoice.charged' | translate"><div class="state">
                    @if (trip.included) {
                      @if (excluding === trip.id) {
                        <input nbInput fieldSize="small" maxlength="255" [placeholder]="'invoice.reason' | translate" [attr.aria-label]="'invoice.reason' | translate" [(ngModel)]="reason" (keydown.enter)="saveTrip(trip, false)">
                        <button nbButton size="tiny" status="primary" [disabled]="busy" (click)="saveTrip(trip, false)">{{ 'invoice.leave_off' | translate }}</button>
                        <button nbButton size="tiny" ghost (click)="excluding = null">{{ 'cancel' | translate }}</button>
                      } @else {
                        <span>{{ 'invoice.included' | translate }}</span>
                        @if (draft) { <button nbButton size="tiny" ghost status="basic" [disabled]="busy" (click)="exclude(trip)">{{ 'invoice.leave_off' | translate }}</button> }
                      }
                    } @else {
                      <span>{{ 'invoice.excluded' | translate:{name: trip.excluded_by ?? '?', date: (trip.excluded_at | localedate:'D MMM YYYY HH:mm')} }}</span>
                      @if (trip.excluded_reason) { <span class="hint">{{ trip.excluded_reason }}</span> }
                      @if (draft) { <button nbButton size="tiny" ghost status="primary" [disabled]="busy" (click)="saveTrip(trip, true)">{{ 'invoice.put_back' | translate }}</button> }
                    }
                  </div></td>
                </tr>
              }
              @for (trip of inv.not_chargeable ?? []; track $index) {
                <tr class="grey">
                  <td class="nowrap">{{ trip.date | localedate:'ddd D MMM' }}</td><td [attr.data-label]="'invoice.person' | translate">{{ trip.person }}</td><td [attr.data-label]="'invoice.project' | translate">{{ trip.project }}</td>
                  <td [attr.data-label]="'invoice.from' | translate">{{ trip.from_address }}</td><td [attr.data-label]="'invoice.to' | translate">{{ trip.to_address }}</td><td class="num" [attr.data-label]="'invoice.km' | translate">{{ trip.km_tenths / 10 | number:'1.1-1':lang }}</td>
                  <td [attr.data-label]="'invoice.charged' | translate">{{ 'invoice.not_chargeable.' + trip.reason | translate }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </details>
    }

    <details class="card history">
      <summary><h2><nb-icon icon="clock-outline"></nb-icon>{{ 'invoice.history' | translate }}<nb-icon class="chevron" icon="chevron-right-outline"></nb-icon></h2></summary>
      <ul class="events">
        @for (e of inv.events; track $index) {
          <li>
            <span class="muted nowrap">{{ e.at | localedate:'D MMM YYYY HH:mm' }}</span>
            <span>{{ e.actor ?? ('invoice.system' | translate) }}</span>
            <span>{{ eventKey(e) | translate:eventParams(e) }}</span>
            @if (e.data?.sha256) { <code>{{ e.data.sha256 | slice:0:12 }}</code> }
          </li>
        }
      </ul>
      @if (inv.pdf_stored) { <p class="sha"><span class="muted">{{ 'invoice.pdf_sha256' | translate }}</span> <code>{{ inv.pdf_sha256 }}</code></p> }
    </details>

    @if (draft) {
      <div class="discard">
        <button id="invoice-delete-btn" nbButton size="small" shape="semi-round" status="danger" ghost [disabled]="busy" [nbTooltip]="'invoice.delete_hint' | translate" (click)="remove()">{{ 'invoice.delete' | translate }}</button>
      </div>
    }
  </article>
}
